import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Form, Row, Col, DatePicker, Button, Table, Input, notification, Card, Divider, Spin, Alert } from 'antd';
import { EyeOutlined, SendOutlined, ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ManagerSelect from '../../ManagerSelect';
import useClientInformForm from '../../../hooks/useClientInformForm';
import useClientInformRecipients from '../../../hooks/useClientInformRecipients';
import { RecipientsCell, RecipientsSummary } from './RecipientsDisplay';
import { clientInformApi, getBuildingUnitId, formatDateRangeForApi, formatRruleDays, groupRowsBySignature } from '../../../api/clientInformApi';
import { getActiveDaysArray, getTheActiveDays } from '../../helpers/schedule';
import { getCurrentUserEmployeeId } from '../managerNameOverride';
import { coreApi } from '../../../api/calls';
import { sortPartners } from '../../helpers/sort';
import EditableSubstitutePartnerCell from './EditableSubstitutePartnerCell';
import EditableSubstituteRruleCell from './EditableSubstituteRruleCell';

const { RangePicker } = DatePicker;

const REQUIRED_FIELDS = ['partner_id', 'substitute_partner_id', 'rrule', 'substitute_rrule'];

/**
 * Modal for informing clients about permanent partner replacement
 */
const PartnerReplacementModal = ({ visible, onClose, selectedUnits, editable = false }) => {
  const { t } = useTranslation();
  const defaultManagerEmployeeId = getCurrentUserEmployeeId();
  const [partners, setPartners] = useState([]);

  const {
    form,
    isSubmitting,
    showConfirmation,
    setIsSubmitting,
    setShowConfirmation,
    applyBulkTimeRange,
    resetForm,
    goBackToForm,
  } = useClientInformForm(selectedUnits, REQUIRED_FIELDS);

  const [tableData, setTableData] = useState([]);
  const { contactsByUnitId, loading: loadingContacts } = useClientInformRecipients(selectedUnits, visible);
  const [previewGroups, setPreviewGroups] = useState([]);
  const watchedChangeWeek = Form.useWatch('change_week', form);
  const watchedSubstitutePartnerId = Form.useWatch('substitute_partner_id', form);
  const watchedSubstituteRrule = Form.useWatch('substitute_rrule', form);

  useEffect(() => {
    if (!editable || !visible || partners.length) return;

    coreApi.get('/partners/all')
      .then((res) => setPartners(sortPartners(res.data)))
      .catch(() => {
        notification.error({ message: t('common.loadFailed') });
      });
  }, [editable, visible, partners.length, t]);

  const getEffectiveUnit = useCallback((unit) => {
    if (!editable) return unit;

    const formPartnerId = watchedSubstitutePartnerId?.[unit.id];
    const formRrule = watchedSubstituteRrule?.[unit.id];

    return {
      ...unit,
      substitute_partner_id: formPartnerId ?? unit.substitute_partner_id,
      substitute_partner: formPartnerId
        ? (partners.find((p) => p.id === formPartnerId) || unit.substitute_partner)
        : unit.substitute_partner,
      substitute_rrule: formRrule ?? unit.substitute_rrule,
    };
  }, [editable, watchedSubstitutePartnerId, watchedSubstituteRrule, partners]);


  const validateRequiredData = useCallback(() => {
    if (!selectedUnits?.length) {
      return { valid: false, errors: [] };
    }

    const errors = [];

    selectedUnits.forEach((unit, index) => {
      const effectiveUnit = getEffectiveUnit(unit);
      const missingFields = [];
      const address = `${unit?.building_unit?.street || ''} ${unit?.building_unit?.house_number || ''}`.trim() || `Jednotka ${index + 1}`;

      if (!effectiveUnit.partner_id && !effectiveUnit.partner?.id) {
        missingFields.push(t('clientInform.missingOriginalPartner'));
      }

      if (!effectiveUnit.substitute_partner_id && !effectiveUnit.substitute_partner?.id) {
        missingFields.push(t('clientInform.missingNewPartner'));
      }

      if (!effectiveUnit.substitute_rrule) {
        missingFields.push(t('clientInform.missingNewCleaningDays'));
      }

      if (missingFields.length > 0) {
        errors.push({
          address,
          fields: missingFields,
        });
      }
    });

    return { valid: errors.length === 0, errors };
  }, [selectedUnits, getEffectiveUnit]);

  useEffect(() => {
    if (visible && selectedUnits?.length) {
      const data = selectedUnits.map((unit) => ({
        key: unit.id,
        id: unit.id,
        unit: unit,
      }));
      
      setTableData(data);
      form.setFieldValue('manager_employee_id', defaultManagerEmployeeId);
    }
  }, [visible, selectedUnits, defaultManagerEmployeeId, form]);

  useEffect(() => {
    if (!visible) {
      resetForm();
      setTableData([]);
      setPreviewGroups([]);
    }
  }, [visible, resetForm]);

  const getNewDays = useCallback((unit) => {
    return unit.substitute_rrule ? formatRruleDays(unit.substitute_rrule) : '-';
  }, []);

  const getNewPartnerName = useCallback((unit) => {
    if (!unit.substitute_partner) return '-';

    const firstName = unit.substitute_partner.first_name || '';
    const lastName = unit.substitute_partner.last_name || '';
    return `${firstName} ${lastName}`.trim() || '-';
  }, []);

  // Group by what actually ends up in the email body: new partner, new cleaning day, week.
  // The original (outgoing) partner_id is not part of the email text - see
  // ClientInformNotification::getPartnerChangeDescription() - so it must NOT split rows
  // into separate groups, or units that would produce an identical email get fragmented.
  const rowGroups = useMemo(() => (
    groupRowsBySignature(tableData, (row) => {
      const unit = getEffectiveUnit(row.unit);
      const week = formatDateRangeForApi(watchedChangeWeek?.[unit.id]);
      return [
        unit.substitute_partner_id ?? unit.substitute_partner?.id,
        unit.substitute_rrule ?? '',
        week,
      ].join('|');
    })
  ), [tableData, watchedChangeWeek, getEffectiveUnit]);

  const buildGroupLabel = useCallback((rows) => {
    const addresses = rows
      .map((row) => {
        const bu = row.unit.building_unit;
        return `${bu?.street || ''} ${bu?.house_number || ''}`.trim();
      })
      .filter(Boolean)
      .join(', ');

    const firstUnit = getEffectiveUnit(rows[0].unit);
    const partnerName = getNewPartnerName(firstUnit);
    const days = getNewDays(firstUnit);
    const week = formatDateRangeForApi(watchedChangeWeek?.[firstUnit.id]) || '-';

    return `${addresses || '-'} — ${partnerName}, ${week}, ${days}`;
  }, [watchedChangeWeek, getNewPartnerName, getNewDays, getEffectiveUnit]);

  const buildPayloadGroups = useCallback(() => {
    const values = form.getFieldsValue();

    return rowGroups.map((rows) => {
      const firstUnit = getEffectiveUnit(rows[0].unit);

      const payload = {
        type: 'switch',
        building_units: rows.map(row => row.unit.building_unit_id || row.unit.building_unit?.id),
        partner_id: firstUnit?.partner_id || firstUnit?.partner?.id,
        partner: firstUnit?.substitute_partner_id || firstUnit?.substitute_partner?.id,
        manager_employee_id: values.manager_employee_id || null,
      };

      const firstUnitWeek = values.change_week?.[firstUnit?.id];
      if (firstUnitWeek) {
        payload.week = formatDateRangeForApi(firstUnitWeek);
      }

      const substituteRrule = firstUnit?.substitute_rrule;
      if (substituteRrule) {
        payload.days = formatRruleDays(substituteRrule);
      }

      return { payload, units: rows.map(row => row.unit), label: buildGroupLabel(rows) };
    });
  }, [form, rowGroups, buildGroupLabel, getEffectiveUnit]);

  const fetchPreviewGroups = useCallback(async () => {
    const groups = buildPayloadGroups();
    const responses = await Promise.all(
      groups.map(group => clientInformApi.preview(group.payload))
    );

    return groups.map((group, index) => ({
      key: group.label,
      label: group.label,
      html: responses[index]?.data?.html || '',
      recipients: responses[index]?.data?.recipients || [],
      buildingsWithoutContacts: responses[index]?.data?.buildings_without_contacts || [],
    }));
  }, [buildPayloadGroups]);

  const handleValidateAndPreview = async () => {
    try {
      setIsSubmitting(true);
      
      // First validate required data from selectedUnits
      const validation = validateRequiredData();
      if (!validation.valid) {
        const errorList = validation.errors.map(err => 
          `• ${err.address}: chybí ${err.fields.join(', ')}`
        ).join('\n');
        
        notification.error({
          message: t('clientInform.missingData'),
          description: (
            <div>
              <p style={{ marginBottom: 8 }}>{t('clientInform.missingDataDescription')}</p>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginBottom: 0 }}>
                {errorList}
              </pre>
              <p style={{ marginTop: 8, marginBottom: 0 }}>
                {t('clientInform.missingDataNote')}
              </p>
            </div>
          ),
          duration: 10,
        });
        return;
      }

      // Then validate form fields
      await form.validateFields();

      const groups = await fetchPreviewGroups();
      setPreviewGroups(groups);
      setShowConfirmation(true);

    } catch (error) {
      console.error('Validation or preview failed:', error);
      
      if (error.errorFields) {
        notification.error({
          message: t('clientInform.validationError'),
          description: t('clientInform.fillAllRequired'),
        });
      } else {
        notification.error({
          message: t('clientInform.previewError'),
          description: error?.response?.data?.message || t('clientInform.previewFailed'),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async () => {
    try {
      setIsSubmitting(true);
      await form.validateFields();

      const groups = await fetchPreviewGroups();
      setPreviewGroups(groups);
    } catch (error) {
      console.error('Preview failed:', error);
      notification.error({
        message: t('clientInform.previewError'),
        description: error?.response?.data?.message || t('clientInform.previewFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSend = async () => {
    try {
      setIsSubmitting(true);
      await form.validateFields();

      const groups = buildPayloadGroups();
      const results = await Promise.allSettled(
        groups.map(group => clientInformApi.send(group.payload))
      );

      const succeeded = [];
      const failed = [];

      results.forEach((result, index) => {
        const group = groups[index];
        if (result.status === 'fulfilled' && result.value.data?.success) {
          succeeded.push({ group, message: result.value.data.message || t('clientInform.emailsSentSuccess') });
        } else {
          const message = result.status === 'fulfilled'
            ? result.value.data?.message
            : result.reason?.response?.data?.message;
          failed.push({ group, message: message || t('clientInform.emailsSendFailed') });
        }
      });

      if (failed.length === 0) {
        notification.success({
          message: t('clientInform.sendSuccess'),
          description: (
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {succeeded.map(({ group, message }, idx) => (
                <li key={idx}>{group.label}: {message}</li>
              ))}
            </ul>
          ),
        });
        onClose();
      } else {
        notification.warning({
          message: succeeded.length > 0 ? t('clientInform.sendPartialSuccess') : t('clientInform.sendError'),
          description: (
            <div>
              {succeeded.length > 0 && (
                <>
                  <p style={{ marginBottom: 4 }}>{t('clientInform.sendSuccess')}:</p>
                  <ul style={{ marginTop: 0, marginBottom: 8, paddingLeft: 20 }}>
                    {succeeded.map(({ group, message }, idx) => (
                      <li key={idx}>{group.label}: {message}</li>
                    ))}
                  </ul>
                </>
              )}
              <p style={{ marginBottom: 4 }}>{t('clientInform.sendError')}:</p>
              <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                {failed.map(({ group, message }, idx) => (
                  <li key={idx}>{group.label}: {message}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 0,
        });
      }
    } catch (error) {
      console.error('Send failed:', error);
      notification.error({
        message: t('clientInform.sendError'),
        description: error?.response?.data?.message || t('clientInform.emailsSendFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContactsCell = useCallback((unit) => (
    <RecipientsCell
      contacts={contactsByUnitId.get(getBuildingUnitId(unit))}
      loading={loadingContacts}
    />
  ), [contactsByUnitId, loadingContacts]);

  const getOriginalDays = useCallback((unit) => (
    getTheActiveDays(unit, t) || '-'
  ), [t]);

  const columns = useMemo(() => [
    {
      title: t('clientInform.address'),
      dataIndex: 'unit',
      key: 'address',
      width: 200,
      render: (unit) => {
        const street = unit?.building_unit?.street || '';
        const houseNumber = unit?.building_unit?.house_number || '';
        const fullAddress = `${street} ${houseNumber}`.trim() || '-';
        
        return (
          <Input 
            disabled 
            value={fullAddress}
          />
        );
      },
    },
    {
      title: t('clientInform.emailRecipients'),
      dataIndex: 'unit',
      key: 'contacts',
      width: 200,
      render: renderContactsCell,
    },
    {
      title: t('clientInform.originalCleaningDay'),
      dataIndex: 'unit',
      key: 'original_days',
      width: 120,
      render: (unit) => {
        const originalDays = getOriginalDays(unit);
        const isMissing = !unit.rrule && (!unit.building_unit?.cleaning_days || unit.building_unit.cleaning_days.length === 0);
        return (
          <Input 
            disabled 
            value={originalDays}
            style={isMissing ? { borderColor: '#ff4d4f', backgroundColor: '#fff2f0' } : {}}
          />
        );
      },
    },
    {
      title: t('clientInform.newCleaningDay'),
      dataIndex: 'unit',
      key: 'new_days',
      width: editable ? 220 : 120,
      render: (unit) => {
        if (editable) {
          return (
            <EditableSubstituteRruleCell
              form={form}
              unitId={unit.id}
              initialDays={getActiveDaysArray(unit)}
              validationMessage={t('clientInform.selectNewCleaningDay')}
            />
          );
        }

        const newDays = getNewDays(unit);
        const isMissing = !unit.substitute_rrule;
        return (
          <Input
            disabled
            value={newDays}
            style={isMissing ? { borderColor: '#ff4d4f', backgroundColor: '#fff2f0' } : {}}
          />
        );
      },
    },
    {
      title: t('clientInform.newPartner'),
      dataIndex: 'unit',
      key: 'new_partner',
      width: editable ? 220 : 150,
      render: (unit) => {
        if (editable) {
          return (
            <EditableSubstitutePartnerCell
              unitId={unit.id}
              defaultPartners={partners}
              disabledPartnerId={unit.partner_id || unit.partner?.id}
              validationMessage={t('clientInform.selectNewPartner')}
            />
          );
        }

        const newPartner = getNewPartnerName(unit);
        const isMissing = !unit.substitute_partner_id && !unit.substitute_partner?.id;
        return (
          <Input
            disabled
            value={newPartner}
            style={isMissing ? { borderColor: '#ff4d4f', backgroundColor: '#fff2f0' } : {}}
          />
        );
      },
    },
    {
      title: t('clientInform.changeWeek'),
      dataIndex: 'unit',
      key: 'change_week',
      width: 250,
      render: (unit) => {
        const initialValue = unit.action_week_from && unit.action_week_to
          ? [moment(unit.action_week_from), moment(unit.action_week_to)]
          : null;
        
        return (
          <Form.Item
            name={['change_week', unit.id]}
            initialValue={initialValue}
            rules={[{ required: true, message: t('clientInform.selectChangeWeek') }]}
            style={{ marginBottom: 0 }}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        );
      },
    },
  ], [renderContactsCell, getOriginalDays, getNewDays, getNewPartnerName, editable, partners, form, t]);

  return (
    <Modal
      title={t('clientInform.confirmationPartnerReplacement')}
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <div style={{ display: showConfirmation ? 'none' : 'block' }}>
          {/* Header section with bulk controls */}
          <Row gutter={16}>
              <Col span={4}>
                <Form.Item label={t('clientInform.managerSignature')} name="manager_employee_id">
                  <ManagerSelect
                    withFormItem={false}
                    clearable
                    showSearch
                    activeOnly
                    placeholder={t('clientInform.selectManager')}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t('clientInform.bulkTimeRange')}>
                  <RangePicker
                    style={{ width: '100%' }}
                    onChange={applyBulkTimeRange}
                    placeholder={[t('clientInform.from'), t('clientInform.to')]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {loadingContacts && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Spin indicator={<LoadingOutlined spin />} />
                <span style={{ marginLeft: 8, color: '#999' }}>
                  {t('clientInform.loadingContacts')}
                </span>
              </div>
            )}
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              scroll={{ x: 1200 }}
              size="small"
            />

            <Row justify="end" style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={handleValidateAndPreview}
                loading={isSubmitting}
              >
                {t('clientInform.emailPreview')}
              </Button>
            </Row>
        </div>

        <div style={{ display: showConfirmation ? 'block' : 'none' }}>
          <Row justify="space-between" style={{ marginBottom: 16 }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={goBackToForm}
                disabled={isSubmitting}
              >
                {t('clientInform.backToForm')}
              </Button>
              <div>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  disabled={isSubmitting}
                  style={{ marginRight: 8 }}
                >
                  {t('clientInform.updatePreview')}
                </Button>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={isSubmitting}
                >
                  {t('clientInform.confirmAndSend')}
                </Button>
              </div>
            </Row>

            {previewGroups.length > 1 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('clientInform.groupsDetectedTitle', { count: previewGroups.length })}
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    {previewGroups.map((group, idx) => (
                      <li key={idx}>{group.label}</li>
                    ))}
                  </ul>
                }
              />
            )}

            {previewGroups.map((group, idx) => (
              <Card
                key={group.key || idx}
                title={t('clientInform.groupPreviewLabel', {
                  index: idx + 1,
                  count: previewGroups.length,
                  label: group.label,
                })}
                style={{ marginBottom: 16 }}
              >
                <RecipientsSummary
                  recipients={group.recipients}
                  buildingsWithoutContacts={group.buildingsWithoutContacts}
                />
                <iframe
                  title={`Email Preview ${idx + 1}`}
                  style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
                  srcDoc={group.html}
                />
              </Card>
            ))}
        </div>
      </Form>
    </Modal>
  );
};

export default PartnerReplacementModal;
